import { useState, useEffect } from 'react'
import { useAuth } from '@/lib/contexts/AuthContext'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'
import {
  getPropertyAttributionFirstTouch,
  getPropertyAttributionLastTouch,
  getTimezone,
  type PropertyAttributionRow,
} from '@/lib/api/external-api'
import { parsePeriodToDateRange } from '@/lib/utils/heatmapUtils'
import {
  logAttributionEvent,
  reportAttributionFailure,
} from '@/lib/observability/attribution'

const FLAG_KEY = 'humblytics:attribution:models:v1'

interface Props {
  propertyId: string
  period: string
}

type Model = 'first_touch' | 'last_touch'

export default function AttributionModelPanel({ propertyId, period }: Props) {
  const { token } = useAuth()
  const [model, setModel] = useState<Model>('first_touch')
  const [rows, setRows] = useState<PropertyAttributionRow[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token || !propertyId || !period) return
    let cancelled = false
    setLoading(true)
    setError(null)

    const range = parsePeriodToDateRange(period)
    if (!range) { setLoading(false); return }
    const fn = model === 'first_touch' ? getPropertyAttributionFirstTouch : getPropertyAttributionLastTouch

    fn(
      token,
      propertyId,
      range.startDate.toISOString(),
      range.endDate.toISOString(),
      getTimezone(),
    )
      .then((res) => {
        if (!cancelled) setRows(res.data)
      })
      .catch((err) => {
        reportAttributionFailure('first_last_touch', err, { propertyId }, { model, period })
        if (!cancelled) setError(err.message || 'Failed to load')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [token, propertyId, period, model])

  // Only a deliberate switch is a model change — the effect above also runs on
  // mount and on every period change.
  const handleModelChange = (next: Model) => {
    if (next === model) return
    setModel(next)
    logAttributionEvent('model_changed', { propertyId }, {
      source: 'first_last_touch',
      model: next,
      period,
    })
  }

  const total = rows?.reduce((s, r) => s + r.conversions, 0) ?? 0

  return (
    <Card>
      <CardHeader className="border-b pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-sm font-semibold text-foreground">Attribution model breakdown</CardTitle>
          <Tabs value={model} onValueChange={(v) => handleModelChange(v as Model)}>
            <TabsList className="h-7">
              <TabsTrigger value="first_touch" className="text-xs px-3 h-6">First touch</TabsTrigger>
              <TabsTrigger value="last_touch" className="text-xs px-3 h-6">Last touch</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {loading && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />
            Loading…
          </div>
        )}
        {error && !loading && (
          <p className="text-sm text-destructive py-4">{error}</p>
        )}
        {!loading && !error && rows !== null && (
          <>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No attribution data for this period.</p>
            ) : (
              <div className="rounded-md border border-border/30 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="text-xs font-semibold">Source</TableHead>
                      <TableHead className="text-xs font-semibold">Medium</TableHead>
                      <TableHead className="text-xs font-semibold">Campaign</TableHead>
                      <TableHead className="text-xs font-semibold">Group</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Conversions</TableHead>
                      <TableHead className="text-xs font-semibold text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-sm font-medium">{row.source}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{row.medium || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate" title={row.campaign}>{row.campaign || '—'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[11px]">{row.referrer_group}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-right tabular-nums font-semibold">{row.conversions.toLocaleString()}</TableCell>
                        <TableCell className="text-sm text-right tabular-nums text-muted-foreground">
                          {total > 0 ? `${((row.conversions / total) * 100).toFixed(1)}%` : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function useAttributionModelsFlag() {
  return typeof window !== 'undefined' && window.localStorage.getItem(FLAG_KEY) === 'true'
}
